import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { usePromptUploads } from '@/features/voice-campaigns/api';
import {
  templateFeatures,
  type TtsFlowFormValues,
} from '../../types';

interface Props {
  values: TtsFlowFormValues;
  setValues: (patch: Partial<TtsFlowFormValues>) => void;
}

const DTMF_OPTIONS = [
  '0',
  '1',
  '2',
  '3',
  '4',
  '5',
  '6',
  '7',
  '8',
  '9',
].map((d) => ({ value: d, label: d }));

const MENU_WAIT_OPTIONS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const RE_PROMPT_OPTIONS = ['0', '1', '2', '3'];

/** Sub-list of optional aux files. The required `menu` and (for template 9)
 *  `noAgent` files render separately. */
const OPTIONAL_FILES: ReadonlyArray<{
  key: string;
  label: string;
  toggle: keyof TtsFlowFormValues;
  field: keyof TtsFlowFormValues;
  category: 'welcome' | 'noinput' | 'wronginput' | 'thanks';
}> = [
  {
    key: 'welcome',
    label: 'Welcome file',
    toggle: 'useWelcomeFile',
    field: 'welcomeId',
    category: 'welcome',
  },
  {
    key: 'noinput',
    label: 'No-input file',
    toggle: 'useNoinputFile',
    field: 'noInputId',
    category: 'noinput',
  },
  {
    key: 'wronginput',
    label: 'Wrong-input file',
    toggle: 'useWronginputFile',
    field: 'wrongInputId',
    category: 'wronginput',
  },
  {
    key: 'thanks',
    label: 'Thanks file',
    toggle: 'useThanksFile',
    field: 'thanksId',
    category: 'thanks',
  },
];

function PromptSelect({
  label,
  required,
  value,
  onChange,
  category,
}: {
  label: string;
  required?: boolean;
  value: string;
  onChange: (v: string) => void;
  category: 'welcome' | 'menu' | 'noinput' | 'wronginput' | 'thanks' | 'noagent';
}) {
  const prompts = usePromptUploads();
  const options = (prompts.data ?? [])
    .filter(
      (p) =>
        p.promptCategory === category &&
        (p.promptStatus === undefined ||
          Number(p.promptStatus) === 1 ||
          p.promptStatus === '1'),
    )
    .map((p) => ({
      value: String(p.promptId),
      label: p.fileName || p.promptName || `#${p.promptId}`,
    }));

  return (
    <div className="space-y-2">
      <Label>
        {label}
        {required && <span className="ml-1 text-destructive">*</span>}
      </Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger>
          <SelectValue
            placeholder={
              prompts.isLoading
                ? 'Loading…'
                : options.length === 0
                  ? `No ${category} files uploaded`
                  : 'Pick a voice file'
            }
          />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>
              {o.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

export function StepVoiceDtmf({ values, setValues }: Props) {
  const features = templateFeatures(values.templateId);

  return (
    <div className="space-y-4">
      {/* DTMF picker — template 8 only. Template 9 also collects DTMF, but
          its DTMF is derived from the patch list — no explicit picker. */}
      {values.templateId === '8' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-1">
              DTMF<span className="text-destructive">*</span>
            </CardTitle>
            <CardDescription>
              Digits the menu accepts from the caller.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <MultiSelect
              options={DTMF_OPTIONS}
              value={values.dtmf}
              onChange={(v) => setValues({ dtmf: v })}
              placeholder="Pick accepted DTMF digits"
              searchPlaceholder="Filter…"
            />
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Voice files & menu</CardTitle>
          <CardDescription>
            Pick the menu prompt + how long to wait for a key press. Toggle the
            optional aux files (welcome, no-input, wrong-input, thanks) as
            needed.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-x-6 gap-y-2 rounded-md border border-border bg-muted/30 p-3">
            {OPTIONAL_FILES.map(({ key, label, toggle }) => (
              <label
                key={key}
                className="flex cursor-pointer items-center gap-2 text-sm"
              >
                <Checkbox
                  checked={values[toggle] as boolean}
                  onCheckedChange={(v) =>
                    setValues({ [toggle]: Boolean(v) } as Partial<TtsFlowFormValues>)
                  }
                />
                {label}
              </label>
            ))}
            {features.needsNoAgent && (
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <Checkbox
                  checked={values.useNoAgentFile}
                  onCheckedChange={(v) =>
                    setValues({ useNoAgentFile: Boolean(v) })
                  }
                />
                No-agent file
                <span className="text-[10px] font-semibold uppercase tracking-wide text-destructive">
                  required
                </span>
              </label>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <PromptSelect
              label="Menu"
              required
              category="menu"
              value={values.menuId}
              onChange={(v) => setValues({ menuId: v })}
            />

            <div className="space-y-2">
              <Label>
                Menu wait time (seconds)
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select
                value={values.menuWaitTime}
                onValueChange={(v) => setValues({ menuWaitTime: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick wait time" />
                </SelectTrigger>
                <SelectContent>
                  {MENU_WAIT_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>
                Re-prompt count
                <span className="ml-1 text-destructive">*</span>
              </Label>
              <Select
                value={values.rePrompt}
                onValueChange={(v) => setValues({ rePrompt: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Pick count" />
                </SelectTrigger>
                <SelectContent>
                  {RE_PROMPT_OPTIONS.map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {OPTIONAL_FILES.map(({ key, label, toggle, field, category }) => {
              if (!values[toggle]) return null;
              return (
                <PromptSelect
                  key={key}
                  label={label.replace(' file', '')}
                  category={category}
                  value={String(values[field] ?? '')}
                  onChange={(v) =>
                    setValues({ [field]: v } as Partial<TtsFlowFormValues>)
                  }
                />
              );
            })}

            {features.needsNoAgent && values.useNoAgentFile && (
              <PromptSelect
                label="No agent available"
                required
                category="noagent"
                value={values.noAgentId}
                onChange={(v) => setValues({ noAgentId: v })}
              />
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
